import { demTileCache } from '../terrain/dem-tile-cache.js'
import { landCoverService } from '../terrain/land-cover-service.js'
import { snowDepthService } from '../terrain/snow-depth-service.js'
import { bboxToTileRange } from '../../lib/tile-math.js'
import { logger } from '../../utils/logger.js'
import { extractTrailsInBbox, type TrailSegment } from './trail-extractor.js'
import { rasterizeTrails } from './trail-rasterizer.js'
import { smoothGridPath } from './route-path-refiner.js'
import {
  buildTrailNetwork,
  routeOnTrailNetwork,
  createElevationSampler,
  type TrailNetwork,
  type ElevationSampler,
} from './trail-network.js'
import RoutePlannerWorker from './route-planner-worker.ts?worker'
import type {
  RoutePlanRequest,
  RoutePlanWorkerOutput,
  RouteResult,
  RouteStrategy,
} from './route-planner-types.js'
import { STRATEGY_TRAIL_MULTIPLIERS } from './route-planner-types.js'

const log = logger.create('route-planner')

const DEM_ZOOM = 12
const TILE_SIZE = 256
const BBOX_PADDING = 0.2
const WORKER_IDLE_TIMEOUT_MS = 60_000
const ALL_STRATEGIES: RouteStrategy[] = ['fastest', 'most-trail', 'most-direct']

function decodeTerrariumElevation(r: number, g: number, b: number): number {
  return r * 256 + g + b / 256 - 32768
}

function tileToLng(x: number, z: number): number {
  return (x / Math.pow(2, z)) * 360 - 180
}

function tileToLat(y: number, z: number): number {
  const n = Math.PI - (2 * Math.PI * y) / Math.pow(2, z)
  return (180 / Math.PI) * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n)))
}

function metersPerPixel(lat: number, zoom: number): number {
  return (156543.03392 * Math.cos((lat * Math.PI) / 180)) / Math.pow(2, zoom)
}

interface PendingRequest {
  resolve: (result: RouteResult) => void
  reject: (error: Error) => void
  strategy: RouteStrategy
  onProgress?: (percent: number, nodesExplored: number) => void
}

interface GridData {
  elevationGrid: Float32Array
  trailMask: Uint8Array
  trailSegments: TrailSegment[]
  trailNetwork: TrailNetwork
  elevationSampler: ElevationSampler
  landCoverGrid: Uint8Array | null
  snowDepthGrid: Float32Array | null
  gridWidth: number
  gridHeight: number
  gridWaypoints: { col: number; row: number }[]
  waypoints: [number, number][]
  cellSizeMeters: number
  originLng: number
  originLat: number
  lngPerCol: number
  latPerRow: number
}

class RoutePlannerService {
  private workers: Worker[] = []
  private idleTimer: ReturnType<typeof setTimeout> | null = null
  private nextRequestId = 0
  private pending = new Map<number, PendingRequest>()

  async planRoute(
    request: RoutePlanRequest,
    onProgress?: (percent: number, nodesExplored: number) => void
  ): Promise<RouteResult> {
    const strategy: RouteStrategy = request.strategy ?? 'fastest'
    const grid = await this.buildGridData(request.waypoints)

    if (strategy === 'most-trail') {
      const trailResult = await this.planTrailNetworkRoute(grid)
      if (trailResult) return trailResult
    }

    const result = await this.planGridRoute(grid, strategy, (_, percent) =>
      onProgress?.(percent, 0)
    )
    if (!result) throw new Error(`Route planning failed for strategy ${strategy}`)
    return result
  }

  async planAlternatives(
    waypoints: [number, number][],
    onProgress?: (strategy: RouteStrategy, percent: number) => void
  ): Promise<RouteResult[]> {
    if (waypoints.length < 2) {
      throw new Error('At least 2 waypoints required')
    }

    log.info(`Planning ${ALL_STRATEGIES.length} route alternatives`)

    const grid = await this.buildGridData(waypoints)

    const promises = ALL_STRATEGIES.map(strategy => {
      if (strategy === 'most-trail') {
        return this.planTrailNetworkRoute(grid).catch(err => {
          log.warn('Trail network route failed, falling back to grid A*:', err)
          return this.planGridRoute(grid, strategy, onProgress)
        })
      }
      return this.planGridRoute(grid, strategy, onProgress)
    })

    const results = await Promise.all(promises)
    this.scheduleIdleTermination()
    return results.filter((r): r is RouteResult => r !== null)
  }

  cancel(): void {
    for (const worker of this.workers) {
      worker.terminate()
    }
    this.workers = []

    for (const [, req] of this.pending) {
      req.reject(new Error('Route planning cancelled'))
    }
    this.pending.clear()
  }

  private async buildGridData(waypoints: [number, number][]): Promise<GridData> {
    if (waypoints.length < 2) {
      throw new Error('At least 2 waypoints required')
    }

    const bbox = this.computeBbox(waypoints)
    const { minX, minY, maxX, maxY } = bboxToTileRange(bbox, DEM_ZOOM)
    const tilesX = maxX - minX + 1
    const tilesY = maxY - minY + 1
    const gridWidth = tilesX * TILE_SIZE
    const gridHeight = tilesY * TILE_SIZE

    log.info(
      `Grid: ${gridWidth}×${gridHeight} (${tilesX}×${tilesY} tiles), ${(gridWidth * gridHeight).toLocaleString()} cells`
    )

    const originLng = tileToLng(minX, DEM_ZOOM)
    const originLat = tileToLat(minY, DEM_ZOOM)
    const farLng = tileToLng(maxX + 1, DEM_ZOOM)
    const farLat = tileToLat(maxY + 1, DEM_ZOOM)
    const lngPerCol = (farLng - originLng) / gridWidth
    const latPerRow = (farLat - originLat) / gridHeight
    const centerLat = (originLat + farLat) / 2
    const cellSizeMeters = metersPerPixel(centerLat, DEM_ZOOM)

    const gridWaypoints = waypoints.map(([lng, lat]) => ({
      col: Math.max(0, Math.min(gridWidth - 1, Math.round((lng - originLng) / lngPerCol))),
      row: Math.max(0, Math.min(gridHeight - 1, Math.round((lat - originLat) / latPerRow))),
    }))

    const [elevationGrid, trails, landCoverGrid, snowDepthGrid] = await Promise.all([
      this.buildElevationGrid(minX, minY, maxX, maxY, gridWidth, gridHeight),
      extractTrailsInBbox(bbox),
      landCoverService.queryClassesInBbox(bbox, gridWidth, gridHeight).catch(err => {
        log.warn('Land cover query failed, routing without it:', err)
        return null
      }),
      snowDepthService.queryDepthInBbox(bbox, gridWidth, gridHeight).catch(err => {
        log.warn('Snow depth query failed, routing without it:', err)
        return null
      }),
    ])

    const gridParams = { gridWidth, gridHeight, originLng, originLat, lngPerCol, latPerRow }
    const trailMask = rasterizeTrails(trails, gridParams)
    log.info(`Trail mask: ${trailMask.reduce((sum, v) => sum + v, 0)} cells marked as trail`)
    if (landCoverGrid) log.info('Land cover grid loaded')
    if (snowDepthGrid) log.info('Snow depth grid loaded')

    const elevationSampler = createElevationSampler(
      elevationGrid,
      gridWidth,
      gridHeight,
      originLng,
      originLat,
      lngPerCol,
      latPerRow
    )
    const trailNetwork = buildTrailNetwork(trails, elevationSampler)

    return {
      elevationGrid,
      trailMask,
      trailSegments: trails,
      trailNetwork,
      elevationSampler,
      landCoverGrid,
      snowDepthGrid,
      gridWidth,
      gridHeight,
      gridWaypoints,
      waypoints,
      cellSizeMeters,
      originLng,
      originLat,
      lngPerCol,
      latPerRow,
    }
  }

  private dispatchToWorker(
    grid: GridData,
    strategy: RouteStrategy,
    trailCostMultiplier: number,
    onProgress?: (percent: number, nodesExplored: number) => void
  ): Promise<RouteResult> {
    const worker = this.createWorker()
    const requestId = this.nextRequestId++

    // Copy grid data so each worker gets its own transferable buffers
    const elevCopy = new Float32Array(grid.elevationGrid)
    const trailCopy = new Uint8Array(grid.trailMask)
    const lcCopy = grid.landCoverGrid ? new Uint8Array(grid.landCoverGrid) : null
    const snowCopy = grid.snowDepthGrid ? new Float32Array(grid.snowDepthGrid) : null

    const transferables: ArrayBuffer[] = [elevCopy.buffer, trailCopy.buffer]
    if (lcCopy) transferables.push(lcCopy.buffer)
    if (snowCopy) transferables.push(snowCopy.buffer)

    return new Promise<RouteResult>((resolve, reject) => {
      const pending: PendingRequest = { resolve, reject, strategy }
      if (onProgress) pending.onProgress = onProgress
      this.pending.set(requestId, pending)

      worker.postMessage(
        {
          elevationGrid: elevCopy,
          trailMask: trailCopy,
          landCoverGrid: lcCopy,
          snowDepthGrid: snowCopy,
          gridWidth: grid.gridWidth,
          gridHeight: grid.gridHeight,
          waypoints: grid.gridWaypoints,
          cellSizeMeters: grid.cellSizeMeters,
          trailCostMultiplier,
          originLng: grid.originLng,
          originLat: grid.originLat,
          lngPerCol: grid.lngPerCol,
          latPerRow: grid.latPerRow,
          requestId,
        },
        transferables
      )
    })
  }

  private createWorker(): Worker {
    if (this.idleTimer) {
      clearTimeout(this.idleTimer)
      this.idleTimer = null
    }

    const worker = new RoutePlannerWorker()
    this.workers.push(worker)

    worker.onmessage = (e: MessageEvent<RoutePlanWorkerOutput>) => {
      this.handleWorkerMessage(e.data, worker)
    }
    worker.onerror = (e: ErrorEvent) => {
      log.error('Worker error:', e.message)
      this.terminateWorker(worker)
    }

    return worker
  }

  private handleWorkerMessage(msg: RoutePlanWorkerOutput, worker: Worker): void {
    const req = this.pending.get(msg.requestId)
    if (!req) return

    switch (msg.type) {
      case 'progress':
        if (req.onProgress) {
          req.onProgress(msg.percent, msg.nodesExplored)
        }
        break

      case 'complete': {
        const path: [number, number][] = []
        for (let i = 0; i < msg.path.length; i += 2) {
          path.push([msg.path[i], msg.path[i + 1]])
        }

        this.pending.delete(msg.requestId)
        this.terminateWorker(worker)

        req.resolve({
          path,
          distanceMeters: msg.distanceMeters,
          estimatedTimeMinutes: msg.estimatedTimeMinutes,
          elevationGainMeters: msg.elevationGainMeters,
          elevationLossMeters: msg.elevationLossMeters,
          strategy: req.strategy,
        })
        break
      }

      case 'error':
        log.error(`Route planning failed (${req.strategy}):`, msg.message)
        this.pending.delete(msg.requestId)
        this.terminateWorker(worker)
        req.reject(new Error(msg.message))
        break
    }
  }

  private terminateWorker(worker: Worker): void {
    worker.terminate()
    const idx = this.workers.indexOf(worker)
    if (idx >= 0) this.workers.splice(idx, 1)
  }

  private scheduleIdleTermination(): void {
    if (this.idleTimer) clearTimeout(this.idleTimer)
    this.idleTimer = setTimeout(() => {
      for (const worker of this.workers) {
        worker.terminate()
      }
      this.workers = []
      this.idleTimer = null
    }, WORKER_IDLE_TIMEOUT_MS)
  }

  private async planTrailNetworkRoute(grid: GridData): Promise<RouteResult | null> {
    const start = grid.waypoints[0]
    const end = grid.waypoints[grid.waypoints.length - 1]

    const trailResult = routeOnTrailNetwork(grid.trailNetwork, start, end, grid.elevationSampler)
    if (!trailResult) {
      log.info('No trail network path found')
      return null
    }

    const fullPath: [number, number][] = []

    // Off-trail connection: start → trail entry
    const trailEntry = trailResult.path[0]
    if (haversine(start, trailEntry) > 1) {
      fullPath.push(start)
    }

    for (const p of trailResult.path) fullPath.push(p)

    // Off-trail connection: trail exit → end
    const trailExit = trailResult.path[trailResult.path.length - 1]
    if (haversine(end, trailExit) > 1) {
      fullPath.push(end)
    }

    log.info(
      `Trail network route: ${fullPath.length} points, ` +
        `${(trailResult.distanceMeters / 1000).toFixed(1)} km, ` +
        `${(trailResult.timeSeconds / 60).toFixed(0)} min`
    )

    return {
      path: fullPath,
      distanceMeters: trailResult.distanceMeters,
      estimatedTimeMinutes: trailResult.timeSeconds / 60,
      elevationGainMeters: trailResult.elevationGainMeters,
      elevationLossMeters: trailResult.elevationLossMeters,
      strategy: 'most-trail',
    }
  }

  private async planGridRoute(
    grid: GridData,
    strategy: RouteStrategy,
    onProgress?: (strategy: RouteStrategy, percent: number) => void
  ): Promise<RouteResult | null> {
    const multiplier = STRATEGY_TRAIL_MULTIPLIERS[strategy]
    const progressCb = onProgress ? (percent: number) => onProgress(strategy, percent) : undefined

    try {
      const result = await this.dispatchToWorker(grid, strategy, multiplier, progressCb)
      result.path = smoothGridPath(result.path)
      return result
    } catch (err) {
      log.warn(`Grid A* strategy ${strategy} failed:`, err)
      return null
    }
  }

  private computeBbox(waypoints: [number, number][]): [number, number, number, number] {
    let west = Infinity
    let south = Infinity
    let east = -Infinity
    let north = -Infinity

    for (const [lng, lat] of waypoints) {
      if (lng < west) west = lng
      if (lng > east) east = lng
      if (lat < south) south = lat
      if (lat > north) north = lat
    }

    const dLng = (east - west) * BBOX_PADDING || 0.01
    const dLat = (north - south) * BBOX_PADDING || 0.01

    return [west - dLng, south - dLat, east + dLng, north + dLat]
  }

  private async buildElevationGrid(
    minTileX: number,
    minTileY: number,
    maxTileX: number,
    maxTileY: number,
    gridWidth: number,
    gridHeight: number
  ): Promise<Float32Array> {
    const grid = new Float32Array(gridWidth * gridHeight).fill(NaN)

    const tilePromises: Promise<void>[] = []
    for (let ty = minTileY; ty <= maxTileY; ty++) {
      for (let tx = minTileX; tx <= maxTileX; tx++) {
        tilePromises.push(
          demTileCache.getTile(DEM_ZOOM, tx, ty).then(pixels => {
            if (!pixels) return
            const offsetX = (tx - minTileX) * TILE_SIZE
            const offsetY = (ty - minTileY) * TILE_SIZE

            for (let py = 0; py < TILE_SIZE; py++) {
              for (let px = 0; px < TILE_SIZE; px++) {
                const pixIdx = (py * TILE_SIZE + px) * 4
                const elev = decodeTerrariumElevation(
                  pixels[pixIdx],
                  pixels[pixIdx + 1],
                  pixels[pixIdx + 2]
                )
                const gridIdx = (offsetY + py) * gridWidth + (offsetX + px)
                grid[gridIdx] = elev
              }
            }
          })
        )
      }
    }

    await Promise.all(tilePromises)
    log.info(`Elevation grid built: ${gridWidth}×${gridHeight}`)
    return grid
  }
}

function haversine(a: [number, number], b: [number, number]): number {
  const toRad = Math.PI / 180
  const dlat = (b[1] - a[1]) * toRad
  const dlng = (b[0] - a[0]) * toRad
  const sinDlat = Math.sin(dlat / 2)
  const sinDlng = Math.sin(dlng / 2)
  const h = sinDlat * sinDlat + Math.cos(a[1] * toRad) * Math.cos(b[1] * toRad) * sinDlng * sinDlng
  return 6371000 * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h))
}

export const routePlannerService = new RoutePlannerService()
